import { useState } from "react";
import { useDelete, useNotify } from "ra-core";
import { useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Confirm } from "@/components/admin/confirm";
import type { Task } from "../types";

export function TaskDeleteAction({
  task,
  onDeleted,
  open: controlledOpen,
  onOpenChange,
  hideTrigger = false,
}: {
  task: Task;
  onDeleted: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  hideTrigger?: boolean;
}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen ?? internalOpen;
  const setOpen = onOpenChange ?? setInternalOpen;
  const [remove, { isPending }] = useDelete();
  const notify = useNotify(),
    client = useQueryClient();
  return (
    <>
      {!hideTrigger && (
        <Button
          type="button"
          variant="outline"
          className="min-h-11 text-destructive"
          onClick={() => setOpen(true)}
        >
          <Trash2 className="size-4" />
          Удалить задачу
        </Button>
      )}
      <Confirm
        isOpen={open}
        title="Удалить задачу?"
        content="Задача и её комментарии будут удалены. Это действие нельзя отменить."
        confirm="Удалить"
        loading={isPending}
        onClose={() => {
          if (!isPending) setOpen(false);
        }}
        onConfirm={() =>
          remove(
            "tasks",
            { id: task.id, previousData: task },
            {
              mutationMode: "pessimistic",
              onSuccess: async () => {
                setOpen(false);
                onDeleted();
                notify("Задача удалена", { type: "success" });
                await Promise.all(
                  ["tasks", "contact_tasks", "task_contacts", "contacts"].map(
                    (resource) =>
                      client.invalidateQueries({ queryKey: [resource] }),
                  ),
                );
              },
              onError: (error) =>
                notify(
                  (error as { message?: string })?.message ||
                    "Не удалось удалить задачу",
                  { type: "error" },
                ),
            },
          )
        }
      />
    </>
  );
}
